import React, { useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { Box, useMediaQuery } from "@mui/system";
import { LocationFilter } from "@/types/dto/search-filters/location-filter";
import FilterBox from "./filter-box";

interface LocationFilterProps {
  filterData: LocationFilter[];
  onChange?: (data: Record<string, string>, title?: string) => void;
  shouldClear?: boolean;
  onClearComplete?: () => void;
  isLoading?: boolean;
  onSelectionMade?: () => void;
  currentValues?: string[];
  title?: string; // Add this prop to pass current selected values from search params
}

const LocationWhereFilter = ({
  filterData,
  onChange,
  shouldClear,
  onClearComplete,
  isLoading,
  onSelectionMade,
  currentValues = [],
  title = "Where"
}: LocationFilterProps) => {
  const [inputValue, setInputValue] = useState("");
  // State to track if dropdown is open
  const isDesktop = useMediaQuery("(min-width:768px)");
  const inputRef = React.useRef<HTMLInputElement>(null);

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option: LocationFilter) => (option as LocationFilter).displayName
  });

  // Get filtered options based on current input
  const getFilteredOptions = () => {
    if (!inputValue) return filterData;
    // Manual filtering to match the "any" behavior
    return filterData.filter((option) =>
      option.displayName.toLowerCase().includes(inputValue.toLowerCase())
    );
  };
  // Handle clearing from parent (clear all button)
  useEffect(() => {
    if (shouldClear) {
      setInputValue("");
      if (onClearComplete) {
        onClearComplete();
      }
    }
  }, [shouldClear, onClearComplete]);

  useEffect(() => {
    if (!isDesktop) {
      return;
    }
    const handleScroll = (event: Event) => {
      // Only blur if the scroll event is from the window or document (not dropdown or children)
      if (event.target === document || event.target === document.body || event.target === window) {
        if (inputRef.current) {
          inputRef.current.blur();
        }
      }
    };
    window.addEventListener("scroll", handleScroll, true);
    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isDesktop]);

  return (
    <FilterBox name="Where">
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Stack
          spacing={2}
          sx={{
            width: {
              xs: "100%", // Mobile - full width
              legacyTablet: "100%", // Tablet - full width
              legacyDesktop: 286 // Desktop - fixed width
            }
          }}
        >
          <Autocomplete
            // ...existing code...
            freeSolo
            id="location-where"
            data-testid="location-where-autocomplete"
            disableClearable
            options={filterData}
            filterOptions={filterOptions}
            disabled={isLoading}
            sx={{
              width: "100%" // Make Autocomplete take full width of its container
            }}
            slotProps={{
              paper: {
                sx: {
                  backgroundColor: "background.default",
                  "& ul": {
                    display: "block", // Override any flex behavior
                    "& li": {
                      display: "block", // Ensure list items are block-level
                      whiteSpace: "normal", // Allow text wrapping
                      wordWrap: "break-word", // Break long words if needed
                      "& span": {
                        display: "inline", // Keep spans inline
                        verticalAlign: "baseline" // Consistent alignment
                      }
                    }
                  }
                }
              },
              popper: {
                modifiers: [
                  {
                    name: "flip",
                    enabled: false // Prevents auto-flipping direction
                  }
                ]
              },
              listbox: {
                sx: {
                  display: "block", // Override flex behavior on listbox
                  "& li": {
                    display: "block !important", // Override MUI's flex styles
                    alignItems: "flex-start !important", // If flex is forced, align to start
                    whiteSpace: "normal !important", // Allow wrapping
                    "&.Mui-focused": {
                      backgroundColor: "var(--theme-palette-grey-200)!important"
                    },
                    "& span": {
                      display: "inline !important", // Keep text spans inline
                      verticalAlign: "baseline !important"
                    }
                  }
                }
              }
            }}
            getOptionLabel={(option: string | LocationFilter) =>
              typeof option === "string" ? option : option.displayName
            }
            getOptionDisabled={(option) => {
              // Disable options that are currently in the search parameters
              if (typeof option === "string") return false;
              return currentValues.includes(option.value);
            }}
            renderOption={(props, option) => {
              const key =
                typeof option === "string" ? `custom-${option}` : (option.id ?? option.displayName);
              const displayName = typeof option === "string" ? option : option.displayName;

              // Create underlined content if there's input value and it's not a string option
              let content: React.ReactNode = displayName;
              if (inputValue.length >= 1 && typeof option !== "string") {
                const regex = new RegExp(`(${inputValue})`, "gi");
                const parts = displayName.split(regex);
                let partCounter = 0;
                content = parts.map((part) => {
                  const isMatch = new RegExp(`^${inputValue}$`, "i").test(part);
                  const uniqueKey = `${key}-${isMatch ? "underline" : "plain"}-${partCounter}`;
                  partCounter += 1;
                  return isMatch ? (
                    <span
                      key={uniqueKey}
                      style={{
                        textDecoration: "underline"
                      }}
                    >
                      {part}
                    </span>
                  ) : (
                    <span key={uniqueKey} style={{ margin: 0, padding: 0, display: "inline" }}>
                      {part}
                    </span>
                  );
                });
              }

              return (
                <li {...props} key={key}>
                  {content}
                </li>
              );
            }}
            inputValue={inputValue}
            onInputChange={(_, newInputValue) => {
              setInputValue(newInputValue);
            }}
            onChange={(_, value) => {
              if (!value) return;
              // Only process LocationFilter objects, not strings
              if (typeof value !== "string" && onChange) {
                onChange({ [value.fieldName]: value.value }, title);
                // Notify parent that selection was made
                if (onSelectionMade) {
                  onSelectionMade();
                }
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search"
                inputRef={inputRef}
                disabled={isLoading}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    // Get current filtered options
                    const filteredOptions = getFilteredOptions();

                    // Filter out disabled options (already selected ones)
                    const availableOptions = filteredOptions.filter(
                      (option) => !currentValues.includes(option.value)
                    );

                    // If there's exactly one available option, select it
                    if (availableOptions.length === 1 && onChange) {
                      const singleOption = availableOptions[0];
                      onChange({ [singleOption.fieldName]: singleOption.value }, title);

                      // Clear the input after selection (defer to next tick to avoid MUI conflicts)
                      setTimeout(() => setInputValue(""), 0);

                      // Notify parent that selection was made
                      if (onSelectionMade) {
                        onSelectionMade();
                      }

                      // Prevent default Enter behavior
                      event.preventDefault();
                    }
                  }
                }}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    type: "search"
                  }
                }}
                sx={{ width: "100%" }}
              />
            )}
          />
        </Stack>
      </Box>
    </FilterBox>
  );
};

export default LocationWhereFilter;
